/**
 * Whether the Backoffice copy of a Hub edit agrees with it.
 *
 * The Hub saves an edit first and then pushes it to the Backoffice, so a
 * Backoffice failure never fails the edit — it is reported alongside it. Two
 * shapes carry this:
 *
 * - `BackofficeSync` is the outcome of the push THIS response's edit triggered
 *   (present on the edit's own response only).
 * - `BackofficeSyncMarker` is the persisted state on the row (a report or a
 *   partner assignment): set by the last failed push, cleared by the next
 *   success, so it survives a reload. Admin-only.
 */

export interface BackofficeSync {
  ok: boolean;
  /** False when there was nothing to push to yet (no Backoffice project or
   * partner request exists — the first dispatch sends the current values). */
  attempted: boolean;
  error: string | null;
}

export interface BackofficeSyncMarker {
  error: string | null;
  failedAt: string | null;
}

export interface ApiBackofficeSync {
  ok: boolean;
  attempted?: boolean;
  error?: string | null;
}

export function transformToBackofficeSync(
  api: ApiBackofficeSync | null | undefined
): BackofficeSync | null {
  if (!api) {
    return null;
  }
  return { ok: api.ok, attempted: api.attempted ?? true, error: api.error ?? null };
}

export function transformToBackofficeSyncMarker(
  error: string | null | undefined,
  failedAt: string | null | undefined
): BackofficeSyncMarker {
  return { error: error ?? null, failedAt: failedAt ?? null };
}

/** A push that was attempted and did not land — what the UI calls out. */
export function isBackofficeBehind(sync: BackofficeSync | null | undefined): boolean {
  return sync != null && sync.attempted && !sync.ok;
}
